import { Dispatch } from 'react';
import get from 'lodash.get';
import cloneDeep from 'lodash.clonedeep';
import arrayMove from 'array-move';
import {
  Input, Option, Rule, Statement, StatementGroup,
} from '.';
import { addObjectParentReferences, buildObjectPath } from '../../../utils/rule-object-utils';
import { IApiService } from '../../services/api-service';
import { IAction } from '../root/i-action';
import { IState } from '../root/state.interface';
import { RuleActionType } from './rules.enum';
import removeObjectParentReferences from '../../../utils/rule-object-utils/remove-object-parent-references';
import getStatement from '../../../components/pages/rules/rules/utils/getStatement';

const getIndexFromPathString = (path: string): number => {
  const from = path.lastIndexOf('[');
  const to = path.lastIndexOf(']');
  return parseInt(path.substring(from + 1, to), 10);
};

const rebuildIndexes = (target: Rule[]) => {
  target.forEach((rule: Rule, i: number) => {
    // eslint-disable-next-line
    rule.$$$nodeName = `[${i}]`;
  });
};

export interface IRuleActions {
  getRulesAndStatements(target: string): Promise<void>;

  setRuleValue(value: string, rule: Rule, input: Input | null, target:string): void;

  saveRules(target: string): Promise<void>;

  resetStatementRule(type: string, rule: Rule, target: string): void;

  addIfRule(parent: Rule[], target: string): void;

  addStatementRule(parent: Rule[], target: string): void;

  deleteRule(rule: Rule, target: string): void;

  changeActive(rule: Rule, target: string): void;

  getSetValueOptions(property: string, url: string): Promise<Option[]>;

  moveRule(oldIndex: number, newIndex: number, target: string, rule: Rule | null | undefined): void;

  getSetPropertyInput(url: string): Promise<Input | null>;

  // eslint-disable-next-line
  getTargetStatements(target: string): any;
}

export class RuleActions implements IRuleActions {
  protected readonly dispatch: Dispatch<IAction>;

  private readonly apiService: IApiService;

  private readonly state: IState;

  // eslint-disable-next-line
  private readonly targetBehavior: any = {
    rules: {
      GET_ACTION_REQUEST: RuleActionType.GET_RULES_AND_STATEMENTS_REQUEST,
      GET_ACTION_SUCCESS: RuleActionType.GET_RULES_AND_STATEMENTS_SUCCESS,
      GET_ACTION_FAIL: RuleActionType.GET_RULES_AND_STATEMENTS_FAIL,
      SAVE_ACTION_REQUEST: RuleActionType.UPDATE_RULES_REQUEST,
      SAVE_ACTION_SUCCESS: RuleActionType.UPDATE_RULES_SUCCESS,
      SAVE_ACTION_FAIL: RuleActionType.UPDATE_RULES_FAIL,
      getStateData: (state: IState) => state.rulesData,
    },
    transforms: {
      GET_ACTION_REQUEST: RuleActionType.GET_TRANSFORMS_AND_STATEMENTS_REQUEST,
      GET_ACTION_SUCCESS: RuleActionType.GET_TRANSFORMS_AND_STATEMENTS_SUCCESS,
      GET_ACTION_FAIL: RuleActionType.GET_TRANSFORMS_AND_STATEMENTS_FAIL,
      SAVE_ACTION_REQUEST: RuleActionType.UPDATE_TRANSFORMS_REQUEST,
      SAVE_ACTION_SUCCESS: RuleActionType.UPDATE_TRANSFORMS_SUCCESS,
      SAVE_ACTION_FAIL: RuleActionType.UPDATE_TRANSFORMS_FAIL,
      getStateData: (state: IState) => state.transformsData,
    },
    systemConfig: {
      GET_ACTION_REQUEST: RuleActionType.GET_SYSTEMCONFIG_AND_STATEMENTS_REQUEST,
      GET_ACTION_SUCCESS: RuleActionType.GET_SYSTEMCONFIG_AND_STATEMENTS_SUCCESS,
      GET_ACTION_FAIL: RuleActionType.GET_SYSTEMCONFIG_AND_STATEMENTS_FAIL,
      SAVE_ACTION_REQUEST: RuleActionType.UPDATE_SYSTEMCONFIG_REQUEST,
      SAVE_ACTION_SUCCESS: RuleActionType.UPDATE_SYSTEMCONFIG_SUCCESS,
      SAVE_ACTION_FAIL: RuleActionType.UPDATE_SYSTEMCONFIG_FAIL,
      getStateData: (state: IState) => state.systemConfigData,
    },
    scheduledRules: {
      GET_ACTION_REQUEST: RuleActionType.GET_SCHEDULEDRULES_REQUEST,
      GET_ACTION_SUCCESS: RuleActionType.GET_SCHEDULEDRULES_SUCCESS,
      GET_ACTION_FAIL: RuleActionType.GET_SCHEDULEDRULES_FAIL,
      SAVE_ACTION_REQUEST: RuleActionType.UPDATE_SCHEDULEDRULES_REQUEST,
      SAVE_ACTION_SUCCESS: RuleActionType.UPDATE_SCHEDULEDRULES_SUCCESS,
      SAVE_ACTION_FAIL: RuleActionType.UPDATE_SCHEDULEDRULES_FAIL,
      getStateData: (state: IState) => state.scheduledRulesData,
    },
  };

  public constructor(
    state: IState,
    apiService: IApiService,
    dispatch: Dispatch<IAction>,
  ) {
    this.dispatch = dispatch;
    this.apiService = apiService;
    this.state = state;
  }

  public getRulesAndStatements = async (target: string): Promise<void> => {
    const behavior = this.targetBehavior[target];
    try {
      this.dispatch({ type: behavior.GET_ACTION_REQUEST, payload: null });

      const rules = await this.apiService.get<Rule[]>({ url: `/platform-manager/api/${target}` });
      // const rules = [
      //   {
      //     name: 'if',
      //     active: false,
      //     comment: 'my comment 1234',
      //     condition: {
      //       and: [
      //         {
      //           '==': [
      //             {
      //               var: 'facility',
      //             },
      //             'ZRH',
      //           ],
      //         },
      //       ],
      //     },
      //     then: [
      //       {
      //         active: true,
      //         name: 'drop',
      //       },
      //     ],
      //   },
      //   {
      //     name: 'join',
      //     active: false,
      //     comment: 'joins the AWB to this AWBLU',
      //     key: 'assetType.code',
      //     topic: 'cargologic.ingestion.stage3.awb',
      //     class: 'bollweevil.platform.ingestion.customers.cargologic.AWBJoiner2',
      //   },
      // ];
      // eslint-disable-next-line
      const groups = await this.apiService.get<StatementGroup[]>({
        url: `/platform-manager/api/statements2?target=${target}`,
      });
      const options = groups.map((g) => {
        const opts = g.statements && g.statements.map((s: Statement) => ({
          ...s,
          value: s.name,
        }));

        return {
          label: g.group,
          options: opts,
        };
      });

      addObjectParentReferences(null, null, rules);

      this.dispatch({
        type: behavior.GET_ACTION_SUCCESS,
        payload: { rules, statements: options },
      });
    } catch (error) {
      this.dispatch({ type: behavior.GET_ACTION_FAIL, payload: error });
    }
  };

  setRuleValue = (value: string, rule: Rule, input: Input | null, target: string): void => {
    const { copiedRules, ruleToUpdate } = this.getCopiedRulesAndReferenceToUpdate(rule, target);
    ruleToUpdate[input ? input.name : 'condition'] = value;

    this.updateRulesInState(copiedRules, target);
  };

  saveRules = async (target: string): Promise<void> => {
    const behavior = this.targetBehavior[target];
    try {
      this.dispatch({ type: behavior.SAVE_ACTION_REQUEST, payload: null });
      const { rules } = behavior.getStateData(this.state).data;
      const cloned = cloneDeep(rules);
      removeObjectParentReferences(cloned);

      // eslint-disable-next-line
      await this.apiService.put<any, any>({
        url: `/platform-manager/api/${target}`,
        data: cloned,
      });

      this.dispatch({ type: behavior.SAVE_ACTION_SUCCESS, payload: null });
    } catch (error) {
      this.dispatch({ type: behavior.SAVE_ACTION_FAIL, payload: null });
    }
  };

  resetStatementRule = (type: string, rule: Rule, target: string): void => {
    const { copiedRules, ruleToUpdate } = this.getCopiedRulesAndReferenceToUpdate(rule, target);
    const { statements } = this.targetBehavior[target].getStateData(this.state).data;
    const statement = getStatement(rule.name, statements);

    statement?.inputs?.forEach((input: Input) => {
      if (ruleToUpdate[input.name]) {
        // eslint-disable-next-line no-param-reassign
        delete ruleToUpdate[input.name];
      }
    });
    ruleToUpdate.name = type;

    this.updateRulesInState(copiedRules, target);
  };

  addIfRule = (parent: Rule[], target: string): void => {
    const {
      copiedRules, isRoot, rootIndex, path,
    } = this.getCopiedRulesAndReferenceToUpdate(parent, target);

    // eslint-disable-next-line
    const addIfRuleTo = (destination: any[]) => {
      const c = {
        name: 'if',
        active: true,
        // eslint-disable-next-line
        ['$$$nodeName']: `[${destination.length}]`,
        // eslint-disable-next-line
        ['$$$parentNode']: destination,
        then: [],
      };

      c.then.$$$nodeName = 'then';
      c.then.$$$parentNode = c;
      destination.push(c);
    };

    if (isRoot) {
      addIfRuleTo(copiedRules);
    } else {
      const destination: Rule[] = get(copiedRules[rootIndex], path) as Rule[];
      addIfRuleTo(destination);
    }
    this.updateRulesInState(copiedRules, target);
  };

  addStatementRule = (parent: Rule[], target: string): void => {
    const {
      copiedRules, path, isRoot, rootIndex,
    } = this.getCopiedRulesAndReferenceToUpdate(parent, target);

    if (isRoot) {
      copiedRules.push({
        name: 'drop',
        active: true,
        // eslint-disable-next-line
        ['$$$nodeName']: `[${copiedRules.length}]`,
        // eslint-disable-next-line
        ['$$$parentNode']: copiedRules,
      });
    } else {
      const destination: Rule[] = get(copiedRules[rootIndex], path) as Rule[];
      destination.push({
        name: 'drop',
        active: true,
        // eslint-disable-next-line
        ['$$$nodeName']: `[${destination.length}]`,
        // eslint-disable-next-line
        ['$$$parentNode']: destination,
      });
    }
    this.updateRulesInState(copiedRules, target);
  };

  deleteRule = (rule: Rule, target: string): void => {
    const {
      copiedRules, isRoot, rootIndex, path,
    } = this.getCopiedRulesAndReferenceToUpdate(rule, target);

    if (isRoot) {
      copiedRules.splice(rootIndex, 1);
      rebuildIndexes(copiedRules);
    } else {
      const targetArray: Rule[] = get(copiedRules[rootIndex], path).$$$parentNode as Rule[];
      const index = getIndexFromPathString(path);
      targetArray.splice(index, 1);
      rebuildIndexes(targetArray);
    }

    this.updateRulesInState(copiedRules, target);
  };

  changeActive = (rule: Rule, target: string): void => {
    const {
      copiedRules, isRoot, rootIndex, path,
    } = this.getCopiedRulesAndReferenceToUpdate(rule, target);

    if (isRoot) {
      copiedRules[rootIndex].active = !copiedRules[rootIndex].active;
    } else {
      const targetArray: Rule[] = get(copiedRules[rootIndex], path).$$$parentNode as Rule[];
      const index = getIndexFromPathString(path);
      targetArray[index].active = !targetArray[index].active;
    }

    this.updateRulesInState(copiedRules, target);
  };

  moveRule = (
    oldIndex: number, newIndex: number, targetData: string, rule: Rule | null | undefined,
  ): void => {
    const behavior = this.targetBehavior[targetData];
    const copiedRules = [...behavior.getStateData(this.state).data.rules];
    let target;
    if (rule) {
      const data = this.getCopiedRulesAndReferenceToUpdate(rule, targetData);
      target = data.ruleToUpdate.then;
    } else {
      target = copiedRules;
    }

    if (oldIndex !== newIndex) {
      // eslint-disable-next-line
      const reordered: any[] = arrayMove(target.slice(), oldIndex, newIndex).filter(
        // eslint-disable-next-line
        (el: any) => !!el,
      );
      rebuildIndexes(reordered);
      this.updateRulesInState(reordered, targetData);
    }
  };

  getSetValueOptions = async (property: string, url: string): Promise<Option[]> => {
    try {
      // eslint-disable-next-line
      const result = await this.apiService.get<any>({
        url: `${url}?property=${property}`,
      });

      return Promise.resolve(result.map((r: string) => ({ label: r, value: r })));
    } catch (error) {
      return Promise.resolve([]);
    }
  };

  getSetPropertyInput = async (url: string): Promise<Input | null> => {
    try {
      const result = await this.apiService.get<Input>({
        url: `${url}`,
      });

      return Promise.resolve(result);
    } catch (error) {
      return Promise.resolve(null);
    }
  };

  // eslint-disable-next-line
  getCopiedRulesAndReferenceToUpdate = (node: Rule | Rule[], target: string): any => {

    const behavior = this.targetBehavior[target];
    const copied = [...behavior.getStateData(this.state).data.rules];

    const path = buildObjectPath(node);
    const paths = path.split('.');
    const index: number = getIndexFromPathString(paths[0]);
    const finalPath = paths.slice(1).join('.');

    const data = {
      copiedRules: copied,
      rootIndex: index,
      path: finalPath,
    };

    if (finalPath === '') {
      return {
        ...data,
        ruleToUpdate: copied[index],
        isRoot: true,
      };
    }
    return {
      ...data,
      ruleToUpdate: get(copied[index], finalPath),
      isRoot: false,
    };
  };

  updateRulesInState = (rules: Rule[], target: string): void => {
    const behavior = this.targetBehavior[target];
    const { data } = behavior.getStateData(this.state);
    const payload = { ...data, rules };

    this.dispatch({
      type: behavior.GET_ACTION_SUCCESS,
      payload,
    });
  };

  // eslint-disable-next-line
  getTargetStatements = (target: string): any => this.getTargetBehavior(target).getStateData(this.state).data.statements;

  // eslint-disable-next-line
  getTargetBehavior = (target: string): any => {
    const behavior = this.targetBehavior[target];
    if (!behavior) {
      throw new Error(`No rules target found with name "${target}"`);
    }
    return behavior;
  };
}
